import { CANVAS_SIZE } from "@/features/draw/constants/canvas";
import { hexToInt, rgbToInt } from "../utils/color/colorUtils";
import { canSplit } from "@tiptap/pm/transform";

export interface ProcessedImage {
  pixels: Uint32Array;
  width: number;
  height: number;
}

export function isLittleEndian() {
  const buffer = new ArrayBuffer(4);
  const view = new Uint32Array(buffer);
  const bytes = new Uint8Array(buffer);

  view[0] = 0x12345678;
  return bytes[0] === 0x78;
}

export const useImageProcessing = () => {
  const createTempCanvas = (width: number, height: number) => {
    const canvas = document.createElement("canvas");
    canvas.setAttribute("width", width.toString());
    canvas.setAttribute("height", height.toString());

    return canvas;
  };

  /**
   * 将图片缩放到
   * @param img 图片元素
   * @returns 缩放后的canvas元素
   */
  const resizeImage = (
    img: HTMLImageElement,
    width = CANVAS_SIZE,
    height = CANVAS_SIZE
  ): HTMLCanvasElement => {
    const canvas = createTempCanvas(width, height);
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Failed to get canvas context");
    }
    context.save();

    context.translate(canvas.width / 2, canvas.height / 2);
    context.scale(width / img.width, height / img.height);
    context.drawImage(img, -img.width / 2, -img.height / 2);
    context.restore();

    return canvas;
  };

  const processImage = (img: HTMLImageElement): ProcessedImage => {
    // 1. 将图片转换为CANVAS_SIZE * CANVAS_SIZE的图片
    const resizedCanvas = resizeImage(img);
    // 2. 将图片转换为像素数组
    const canvas = createTempCanvas(resizedCanvas.width, resizedCanvas.height);
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Failed to get canvas context");
    }
    context.drawImage(resizedCanvas, 0, 0);
    const imgData = context.getImageData(
      0,
      0,
      resizedCanvas.width,
      resizedCanvas.height
    ).data;

    // 创建正确大小的像素数组
    const pixels = new Uint32Array(imgData.length / 4);
    for (let i = 0; i < imgData.length; i += 4) {
      pixels[i / 4] = rgbToInt(imgData[i], imgData[i + 1], imgData[i + 2]);
    }

    return {
      pixels,
      width: resizedCanvas.width,
      height: resizedCanvas.height,
    };
  };

  const createImageFromUrl = (url: string): Promise<ProcessedImage> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        try {
          const result = processImage(img);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = url;
    });
  };

  const createImageFromFile = (file: File): Promise<ProcessedImage> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result !== "string") {
          reject(new Error("Failed to read file"));
          return;
        }
        createImageFromUrl(result).then(resolve).catch(reject);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  return {
    processImage,
    createImageFromUrl,
    createImageFromFile,
    createTempCanvas,
  };
};
